import { create } from "zustand";
import { PhoneValidator } from "../utils/phoneValidator";
import { capitalizeFirstLetter } from "../utils/capitalizeLetter";
import { validateCanadaPostalCode } from "../utils/validatePostalCode";
import { validateReferralCodeFormat } from "../utils/validateReferralCode";

export interface PersonalInfoState {
	firstName: string;
	middleName: string;
	email: string;
	lastName: string;
	address: string;
	address2: string;
	city: string;
	postalCode: string;
	province: string;
	phone: string;
	dateOfBirth: Date | null;
	maritalStatus: string;
	isFirstTimeHomeBuyer: boolean | null;
	consent: boolean;
	// totalMonthlyDebt: string;
	hasReferralCode: boolean | null;
	referralCode: string;

	// Errors are not persisted
	firstNameError: string | null;
	lastNameError: string | null;
	addressError: string | null;
	cityError: string | null;
	postalCodeError: string | null;
	provinceError: string | null;
	phoneError: string | null;
	dateOfBirthError: string | null;
	maritalStatusError: string | null;
	isFirstTimeHomeBuyerError: string | null;
	consentError: string | null;
	referralCodeError: string | null;
	hasReferralCodeError: string | null;
	// totalMonthlyDebtError: string | null;

	// Setter methods for each field
	setFirstName: (value: string) => void;
	setEmail: (value: string) => void;
	setMiddleName: (value: string) => void;
	setLastName: (value: string) => void;
	setAddress: (value: string) => void;
	setAddress2: (value: string) => void;
	setCity: (value: string) => void;
	setPostalCode: (value: string) => void;
	setProvince: (value: string) => void;
	setPhone: (value: string) => void;
	setDateOfBirth: (value: Date | null) => void;
	setMaritalStatus: (value: string) => void;
	setIsFirstTimeHomeBuyer: (value: boolean | null) => void;
	setConsent: (value: boolean) => void;
	setHasReferralCode: (value: boolean | null) => void;
	setReferralCode: (value: string) => void;
	// setTotalMonthlyDebt: (value: string) => void;

	// Error setter methods
	setFirstNameError: (error: string | null) => void;
	setLastNameError: (error: string | null) => void;
	setAddressError: (error: string | null) => void;
	setCityError: (error: string | null) => void;
	setPostalCodeError: (error: string | null) => void;
	setProvinceError: (error: string | null) => void;
	setPhoneError: (error: string | null) => void;
	setDateOfBirthError: (error: string | null) => void;
	setMaritalStatusError: (error: string | null) => void;
	setIsFirstTimeHomeBuyerError: (error: string | null) => void;
	// setTotalMonthlyDebtError: (error: string | null) => void;
	setHasReferralCodeError: (error: string | null) => void;
	setReferralCodeError: (error: string | null) => void;
	validatePersonalConsent: () => boolean;

	clearErrors: () => void;
	validatePersonalInfo: () => boolean;
}

export const usePersonalInfoStore = create<PersonalInfoState>()((set, get) => ({
	firstName: "",
	middleName: "",
	lastName: "",
	address: "",
	address2: "",
	city: "",
	postalCode: "",
	province: "",
	email: "",
	phone: "",
	dateOfBirth: null,
	maritalStatus: "",
	// totalMonthlyDebt: "",
	isFirstTimeHomeBuyer: null,
	consent: false,
	hasReferralCode: false,
	referralCode: "",

	// Errors are initialized but will not be persisted
	firstNameError: null,
	lastNameError: null,
	addressError: null,
	cityError: null,
	postalCodeError: null,
	provinceError: null,
	phoneError: null,
	dateOfBirthError: null,
	maritalStatusError: null,
	// totalMonthlyDebtError: null,
	isFirstTimeHomeBuyerError: null,
	consentError: null,
	referralCodeError: null,
	hasReferralCodeError: null,

	// Setters for the fields
	setFirstName: (value) => set({ firstName: capitalizeFirstLetter(value) }),
	setMiddleName: (value) => set({ middleName: capitalizeFirstLetter(value) }),
	setEmail: (value) => set({ email: value }),
	setLastName: (value) => set({ lastName: capitalizeFirstLetter(value) }),
	setAddress: (value) => set({ address: value }),
	setAddress2: (value) => set({ address2: value }),
	setCity: (value) => set({ city: value }),
	setPostalCode: (value) => set({ postalCode: value.toUpperCase() }),
	setProvince: (value) => set({ province: value }),
	// setTotalMonthlyDebt: (value) => set({ totalMonthlyDebt: value }),
	setPhone: (value) => set({ phone: value }),
	setDateOfBirth: (value) => set({ dateOfBirth: value }),
	setMaritalStatus: (value) => set({ maritalStatus: value }),
	setIsFirstTimeHomeBuyer: (value) => set({ isFirstTimeHomeBuyer: value }),
	setReferralCode: (value) => {
		let formattedValue = value.trim();
		if (!formattedValue.startsWith("R")) {
			formattedValue = `R${formattedValue.replace(/\D/g, "")}`;
		}
		set({ referralCode: formattedValue });
	},
	setHasReferralCode: (value) => set({ hasReferralCode: value }),
	// Setters for the errors
	setFirstNameError: (error) => set({ firstNameError: error }),
	setLastNameError: (error) => set({ lastNameError: error }),
	setAddressError: (error) => set({ addressError: error }),
	setCityError: (error) => set({ cityError: error }),
	setPostalCodeError: (error) => set({ postalCodeError: error }),
	setProvinceError: (error) => set({ provinceError: error }),
	setPhoneError: (error) => set({ phoneError: error }),
	setDateOfBirthError: (error) => set({ dateOfBirthError: error }),
	setMaritalStatusError: (error) => set({ maritalStatusError: error }),
	setConsent: (value) => set({ consent: value }),
	setHasReferralCodeError: (error) => set({ hasReferralCodeError: error }),
	// setTotalMonthlyDebtError: (error) => set({ totalMonthlyDebtError: error }),
	setReferralCodeError: (error) => set({ referralCodeError: error }),
	setIsFirstTimeHomeBuyerError: (error) =>
		set({ isFirstTimeHomeBuyerError: error }),

	// Clear all errors
	clearErrors: () =>
		set({
			firstNameError: null,
			lastNameError: null,
			addressError: null,
			cityError: null,
			postalCodeError: null,
			provinceError: null,
			phoneError: null,
			dateOfBirthError: null,
			maritalStatusError: null,
			referralCodeError: null,
			hasReferralCodeError: null,
			// totalMonthlyDebtError: null,
		}),

	validatePersonalConsent: () => {
		const { consent } = get();
		if (!consent) {
			set({ consentError: "Consent is required" });
			return false;
		}
		set({ consentError: null });
		return true;
	},

	validatePersonalInfo: () => {
		const {
			firstName,
			lastName,
			address,
			city,
			postalCode,
			province,
			phone,
			dateOfBirth,
			maritalStatus,
			isFirstTimeHomeBuyer,
			hasReferralCode,
			referralCode,
			// totalMonthlyDebt,

			setFirstNameError,
			setLastNameError,
			setAddressError,
			setCityError,
			setPostalCodeError,
			setProvinceError,
			setPhoneError,
			setDateOfBirthError,
			setMaritalStatusError,
			setIsFirstTimeHomeBuyerError,
			setReferralCodeError,
			setHasReferralCodeError,
			// setTotalMonthlyDebtError,

			clearErrors,
		} = get();

		clearErrors();

		let isValid = true;

		if (!firstName.trim()) {
			setFirstNameError("First name is required");
			isValid = false;
		}

		if (!lastName.trim()) {
			setLastNameError("Last name is required");
			isValid = false;
		}

		if (!address.trim()) {
			setAddressError("Address is required");
			isValid = false;
		}

		if (!city.trim()) {
			setCityError("City is required");
			isValid = false;
		}

		if (!postalCode.trim()) {
			setPostalCodeError("Postal code is required");
			isValid = false;
		} else if (!validateCanadaPostalCode(postalCode)) {
			setPostalCodeError("Invalid Canadian postal code format");
			isValid = false;
		} else {
			setPostalCodeError("");
		}

		if (!province.trim()) {
			setProvinceError("Province is required");
			isValid = false;
		}

		if (phone.trim()) {
			try {
				const isValidPhone =
					PhoneValidator.isValid(phone, "CA") ||
					PhoneValidator.isValid(phone, "US");

				if (!isValidPhone) {
					setPhoneError("Invalid phone number format");
					isValid = false;
				}
			} catch (error) {
				console.error("Phone number validation failed:", error);
				setPhoneError("Invalid phone number format");
				isValid = false;
			}
		} else {
			setPhoneError("Phone number is required");
			isValid = false;
		}

		if (!dateOfBirth) {
			setDateOfBirthError("Date of birth is required");
			isValid = false;
		}

		if (!maritalStatus.trim()) {
			setMaritalStatusError("Marital status is required");
			isValid = false;
		}

		// if (!totalMonthlyDebt.trim()) {
		//   setTotalMonthlyDebtError("Total monthly debt is required");
		//   isValid = false;
		// }

		if (isFirstTimeHomeBuyer === null) {
			setIsFirstTimeHomeBuyerError(
				"Please select if you are a first-time home buyer",
			);
			isValid = false;
		} else {
			setIsFirstTimeHomeBuyerError(null);
		}

		if (hasReferralCode === null) {
			setHasReferralCodeError("Please select if you have a referral code");
			isValid = false;
		} else {
			setHasReferralCodeError(null);
		}

		if (hasReferralCode && !referralCode.trim()) {
			setReferralCodeError("Referral code is required");
			isValid = false;
		} else if (hasReferralCode && !validateReferralCodeFormat(referralCode)) {
			setReferralCodeError(
				"Referral code must be in the format: R123-1234-1234",
			);
			isValid = false;
		} else {
			setReferralCodeError("");
		}

		return isValid;
	},
}));
